import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./IntakePage.css";

// The public intake form (docs/onboarding.md). Mounted from index.js BEFORE Clerk when
// the path is /intake/<token>: the practice has no Atlas account, the link's token is
// the authentication, and the backend answers one 404 for every bad token. Plain
// fetch; the CSRF wrapper only adds a header and the route is exempt.
//
// Credentials typed here go straight to the POST and are cleared from state the moment
// the backend says "received"; nothing is kept in the browser.

const API_URL = process.env.REACT_APP_API_URL || "";

export const INTAKE_PATH_RE = /^\/intake\/[A-Za-z0-9_-]+$/;

export function tokenFromPath(pathname) {
  const m = (pathname || "").match(/^\/intake\/([A-Za-z0-9_-]+)$/);
  return m ? m[1] : null;
}

// Mirrors backend intake_service.npi_error (and PracticeDetailsPage's validateNpi).
export function validateNpi(npi) {
  const digits = (npi || "").replace(/\D/g, "");
  if (digits.length !== 10) return "NPI must be exactly 10 digits";
  const full = "80840" + digits;
  let sum = 0;
  for (let i = full.length - 1; i >= 0; i--) {
    let d = parseInt(full[i], 10);
    if ((full.length - i) % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  if (sum % 10 !== 0) return "Invalid NPI (failed check digit)";
  return null;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EMPTY_ACCESS = {
  access_method: "",
  fields: {},
  username: "",
  password: "",
  mfa_notes: "",
};

const INITIAL = {
  practice: {
    legal_name: "",
    dba: "",
    npi: "",
    tax_id: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    timezone: "",
    specialty: "",
    payer_mix_notes: "",
  },
  contacts: {
    billing_contact_name: "",
    billing_contact_email: "",
    billing_contact_phone: "",
  },
  systems: {
    emr_system: "",
    emr_other: "",
    pm_system: "",
    pm_other: "",
    clearinghouse: "",
    clearinghouse_other: "",
    // Feed spec v2: every clearinghouse the practice uses, with where its portal is,
    // the username Atlas would use and whether Atlas may read 835s there. Never a
    // password: the backend refuses one, and the form has no field for it.
    clearinghouses: [],
    patient_identifier_type: "",
    patient_identifier_other: "",
  },
  access: {
    emr: { ...EMPTY_ACCESS },
    pm: { ...EMPTY_ACCESS },
    clearinghouse: { ...EMPTY_ACCESS },
  },
  baa: { accepted: false, name: "" },
};

const INVALID_MESSAGE =
  "This link is no longer valid. Ask your Atlas contact for a new one.";

const ERA_ACCESS_LABEL = {
  yes: "Yes, Atlas can be given read access to the 835s there",
  no: "No",
  unsure: "Not sure",
};

// A clearinghouse entry as the form keeps it; "none" is not a clearinghouse.
export function emptyClearinghouse(key) {
  return {
    key,
    name: "",
    access_path: "",
    username: "",
    era_access: "",
    notes: "",
  };
}

// Client-side mirror of the backend's validate_submission; the backend is the authority.
export function validateForm(form, definition) {
  const errors = [];
  const p = form.practice;
  if (!p.legal_name.trim()) errors.push("Legal name is required");
  if (!p.npi.trim()) errors.push("NPI is required");
  else {
    const err = validateNpi(p.npi);
    if (err) errors.push(err);
  }
  const taxDigits = p.tax_id.replace(/\D/g, "");
  if (taxDigits && taxDigits.length !== 9)
    errors.push("Tax ID must be 9 digits");
  const phoneDigits = p.phone.replace(/\D/g, "");
  if (phoneDigits && phoneDigits.length !== 10 && phoneDigits.length !== 11)
    errors.push("Phone must be 10 digits");
  if (!p.timezone) errors.push("Timezone is required");
  if (p.state && !/^[A-Za-z]{2}$/.test(p.state.trim()))
    errors.push("State must be the two-letter code");
  if (p.zip && !/^\d{5}(-?\d{4})?$/.test(p.zip.trim()))
    errors.push("ZIP must be 5 or 9 digits");
  if (!form.systems.emr_system) errors.push("EMR system is required");
  (form.systems.clearinghouses || []).forEach((ch, i) => {
    const label = `Clearinghouse ${i + 1}`;
    if (ch.key === "other" && !(ch.name || "").trim())
      errors.push(`${label}: name the clearinghouse`);
    if (!ch.era_access)
      errors.push(
        `${label}: say whether Atlas may read your 835s there (yes, no or unsure)`,
      );
  });
  if (
    form.systems.patient_identifier_type === "other" &&
    !(form.systems.patient_identifier_other || "").trim()
  )
    errors.push("Name the patient identifier your system uses");
  const c = form.contacts;
  if (!c.billing_contact_name.trim())
    errors.push("Billing contact name is required");
  if (!c.billing_contact_email.trim())
    errors.push("Billing contact email is required");
  else if (!EMAIL_RE.test(c.billing_contact_email.trim()))
    errors.push("Billing contact email is not a valid address");
  if (!form.baa.accepted)
    errors.push("The Business Associate Agreement must be accepted");
  if (!form.baa.name.trim())
    errors.push("The name of the person accepting the agreement is required");

  const slots = accessSlots(form, definition);
  slots.forEach(({ slot, systemKey, label }) => {
    const a = form.access[slot];
    if (!a.access_method) return;
    if (a.access_method === "api") {
      const fields = (definition.credential_fields || {})[systemKey] || [];
      fields.forEach((f) => {
        if (f.required && !(a.fields[f.key] || "").trim())
          errors.push(`${label}: ${f.label} is required for API access`);
      });
    } else if (a.access_method === "login") {
      if (!a.username.trim() || !a.password)
        errors.push(
          `${label}: username and password are required for login access`,
        );
    }
  });
  return errors;
}

// Which access sections to show: EMR always (once chosen), PM when it differs from the
// EMR, clearinghouse when Atlas holds keys for it (API only).
export function accessSlots(form, definition) {
  const out = [];
  const { emr_system, pm_system, clearinghouse } = form.systems;
  const labelOf = (key, list) => {
    const hit = (list || []).find((s) => s.key === key);
    return hit ? hit.label : key;
  };
  if (emr_system && emr_system !== "other") {
    out.push({
      slot: "emr",
      systemKey: emr_system,
      label: `EMR (${labelOf(emr_system, definition.systems)})`,
      loginAllowed: true,
    });
  }
  if (pm_system && pm_system !== "other" && pm_system !== emr_system) {
    out.push({
      slot: "pm",
      systemKey: pm_system,
      label: `Practice management (${labelOf(pm_system, definition.systems)})`,
      loginAllowed: true,
    });
  }
  if (clearinghouse && (definition.credential_fields || {})[clearinghouse]) {
    out.push({
      slot: "clearinghouse",
      systemKey: clearinghouse,
      label: `Clearinghouse (${labelOf(clearinghouse, definition.clearinghouses)})`,
      loginAllowed: false,
    });
  }
  return out;
}

function buildPayload(form, definition) {
  const slots = accessSlots(form, definition);
  const access = {};
  slots.forEach(({ slot }) => {
    const a = form.access[slot];
    if (!a.access_method) return;
    if (a.access_method === "api")
      access[slot] = { access_method: "api", fields: a.fields };
    else
      access[slot] = {
        access_method: "login",
        username: a.username,
        password: a.password,
        mfa_notes: a.mfa_notes,
      };
  });
  return {
    practice: form.practice,
    contacts: form.contacts,
    systems: {
      ...form.systems,
      clearinghouses: (form.systems.clearinghouses || []).map((ch) => ({
        key: ch.key,
        name: ch.name,
        access_path: ch.access_path,
        username: ch.username,
        era_access: ch.era_access,
        notes: ch.notes,
      })),
    },
    access,
    baa: form.baa,
  };
}

function IntakePage() {
  const token = useMemo(() => tokenFromPath(window.location.pathname), []);
  const [definition, setDefinition] = useState(null);
  const [state, setState] = useState("loading"); // loading | form | submitting | received | invalid
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState([]);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/intake/${token}`);
        if (!resp.ok) {
          if (!cancelled) setState("invalid");
          return;
        }
        const data = await resp.json();
        if (!cancelled) {
          setDefinition(data);
          setState("form");
        }
      } catch (err) {
        if (!cancelled) setState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const setPractice = (key, value) =>
    setForm((f) => ({ ...f, practice: { ...f.practice, [key]: value } }));
  const setContact = (key, value) =>
    setForm((f) => ({ ...f, contacts: { ...f.contacts, [key]: value } }));
  const setSystem = (key, value) =>
    setForm((f) => ({ ...f, systems: { ...f.systems, [key]: value } }));
  const toggleClearinghouse = (key, on) =>
    setForm((f) => {
      const list = f.systems.clearinghouses || [];
      const has = list.some((ch) => ch.key === key);
      if (on && !has)
        return {
          ...f,
          systems: {
            ...f.systems,
            clearinghouses: [...list, emptyClearinghouse(key)],
          },
        };
      if (!on && has)
        return {
          ...f,
          systems: {
            ...f.systems,
            clearinghouses: list.filter((ch) => ch.key !== key),
          },
        };
      return f;
    });
  const setClearinghouse = (key, patch) =>
    setForm((f) => ({
      ...f,
      systems: {
        ...f.systems,
        clearinghouses: (f.systems.clearinghouses || []).map((ch) =>
          ch.key === key ? { ...ch, ...patch } : ch,
        ),
      },
    }));
  const setAccess = (slot, patch) =>
    setForm((f) => ({
      ...f,
      access: { ...f.access, [slot]: { ...f.access[slot], ...patch } },
    }));
  const setAccessField = (slot, key, value) =>
    setForm((f) => ({
      ...f,
      access: {
        ...f.access,
        [slot]: {
          ...f.access[slot],
          fields: { ...f.access[slot].fields, [key]: value },
        },
      },
    }));

  // jsdom has no scrollTo; a missing one must not break the submit.
  const scrollTop = () => {
    try {
      window.scrollTo(0, 0);
    } catch (_) {
      /* not implemented */
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const clientErrors = validateForm(form, definition);
      if (clientErrors.length) {
        setErrors(clientErrors);
        setMessage(null);
        scrollTop();
        return;
      }
      setErrors([]);
      setMessage(null);
      setState("submitting");
      try {
        const resp = await fetch(`${API_URL}/api/intake/${token}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildPayload(form, definition)),
        });
        if (resp.status === 404) {
          setForm(INITIAL);
          setState("invalid");
          return;
        }
        let body = {};
        try {
          body = await resp.json();
        } catch (_) {
          /* not JSON */
        }
        if (resp.status === 201 && body.ok) {
          // Credentials leave the page with the response.
          setForm(INITIAL);
          setState("received");
          return;
        }
        if (resp.status === 400 && Array.isArray(body.errors)) {
          setErrors(body.errors);
        } else {
          setMessage(
            body.error || `The form could not be saved (HTTP ${resp.status})`,
          );
        }
        setState("form");
        scrollTop();
      } catch (err) {
        setMessage(
          "The form could not be sent. Check your connection and try again.",
        );
        setState("form");
      }
    },
    [form, definition, token],
  );

  if (state === "loading") {
    return (
      <div className="intake-page">
        <div className="intake-card">
          <p className="intake-muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="intake-page">
        <div className="intake-card">
          <h1>Atlas practice intake</h1>
          <div className="form-message error">{INVALID_MESSAGE}</div>
        </div>
      </div>
    );
  }

  if (state === "received") {
    return (
      <div className="intake-page">
        <div className="intake-card">
          <h1>Received</h1>
          <div className="form-message success">
            Thank you. Your practice details are with the Atlas team. We will be
            in touch about connecting your data feeds; nothing more is needed
            from you now.
          </div>
        </div>
      </div>
    );
  }

  const slots = accessSlots(form, definition);
  const submitting = state === "submitting";

  return (
    <div className="intake-page">
      <form className="intake-card" onSubmit={handleSubmit} noValidate>
        <h1>Atlas practice intake</h1>
        <p className="intake-muted">
          This link expires{" "}
          {new Date(definition.expires_at).toLocaleDateString()}. Everything you
          enter is encrypted and seen only by the Atlas billing team.
        </p>

        {message && (
          <div className="form-message error dismissable">
            <span className="message-content">{message}</span>
            <button
              type="button"
              className="message-close"
              onClick={() => setMessage(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        {errors.length > 0 && (
          <div className="form-message error" role="alert">
            <strong>Please correct the following:</strong>
            <ul className="intake-error-list">
              {errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        <h2>Practice</h2>
        <div className="form-row">
          <div className="form-field full">
            <label htmlFor="legal_name">Legal name *</label>
            <input
              id="legal_name"
              value={form.practice.legal_name}
              onChange={(e) => setPractice("legal_name", e.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="dba">Doing business as</label>
            <input
              id="dba"
              value={form.practice.dba}
              onChange={(e) => setPractice("dba", e.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="specialty">Specialty</label>
            <input
              id="specialty"
              value={form.practice.specialty}
              onChange={(e) => setPractice("specialty", e.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="npi">Organization NPI (type 2) *</label>
            <input
              id="npi"
              inputMode="numeric"
              value={form.practice.npi}
              onChange={(e) => setPractice("npi", e.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="tax_id">Tax ID (EIN)</label>
            <input
              id="tax_id"
              inputMode="numeric"
              placeholder="XX-XXXXXXX"
              value={form.practice.tax_id}
              onChange={(e) => setPractice("tax_id", e.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field full">
            <label htmlFor="address1">Address</label>
            <input
              id="address1"
              value={form.practice.address1}
              onChange={(e) => setPractice("address1", e.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field full">
            <label htmlFor="address2">Address line 2</label>
            <input
              id="address2"
              value={form.practice.address2}
              onChange={(e) => setPractice("address2", e.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="city">City</label>
            <input
              id="city"
              value={form.practice.city}
              onChange={(e) => setPractice("city", e.target.value)}
            />
          </div>
          <div className="form-field small">
            <label htmlFor="state">State</label>
            <input
              id="state"
              maxLength={2}
              value={form.practice.state}
              onChange={(e) => setPractice("state", e.target.value)}
            />
          </div>
          <div className="form-field small">
            <label htmlFor="zip">ZIP</label>
            <input
              id="zip"
              value={form.practice.zip}
              onChange={(e) => setPractice("zip", e.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="phone">Practice phone</label>
            <input
              id="phone"
              inputMode="tel"
              value={form.practice.phone}
              onChange={(e) => setPractice("phone", e.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="timezone">Timezone *</label>
            <select
              id="timezone"
              value={form.practice.timezone}
              onChange={(e) => setPractice("timezone", e.target.value)}
            >
              <option value="">Select…</option>
              {(definition.timezones || []).map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field full">
            <label htmlFor="payer_mix_notes">
              Payer mix (which insurers you bill most, roughly)
            </label>
            <textarea
              id="payer_mix_notes"
              rows={3}
              value={form.practice.payer_mix_notes}
              onChange={(e) => setPractice("payer_mix_notes", e.target.value)}
            />
          </div>
        </div>

        <h2>Billing contact</h2>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="billing_contact_name">Name *</label>
            <input
              id="billing_contact_name"
              value={form.contacts.billing_contact_name}
              onChange={(e) =>
                setContact("billing_contact_name", e.target.value)
              }
            />
          </div>
          <div className="form-field">
            <label htmlFor="billing_contact_email">Email *</label>
            <input
              id="billing_contact_email"
              type="email"
              value={form.contacts.billing_contact_email}
              onChange={(e) =>
                setContact("billing_contact_email", e.target.value)
              }
            />
          </div>
          <div className="form-field">
            <label htmlFor="billing_contact_phone">Phone</label>
            <input
              id="billing_contact_phone"
              inputMode="tel"
              value={form.contacts.billing_contact_phone}
              onChange={(e) =>
                setContact("billing_contact_phone", e.target.value)
              }
            />
          </div>
        </div>

        <h2>Systems</h2>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="emr_system">EMR *</label>
            <select
              id="emr_system"
              value={form.systems.emr_system}
              onChange={(e) => setSystem("emr_system", e.target.value)}
            >
              <option value="">Select…</option>
              {definition.systems
                .filter((s) => s.kind !== "pm")
                .map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
            </select>
          </div>
          {form.systems.emr_system === "other" && (
            <div className="form-field">
              <label htmlFor="emr_other">EMR name</label>
              <input
                id="emr_other"
                value={form.systems.emr_other}
                onChange={(e) => setSystem("emr_other", e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="pm_system">
              Practice management / billing (if different)
            </label>
            <select
              id="pm_system"
              value={form.systems.pm_system}
              onChange={(e) => setSystem("pm_system", e.target.value)}
            >
              <option value="">Same as the EMR</option>
              {definition.systems
                .filter((s) => s.kind !== "emr")
                .map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
            </select>
          </div>
          {form.systems.pm_system === "other" && (
            <div className="form-field">
              <label htmlFor="pm_other">System name</label>
              <input
                id="pm_other"
                value={form.systems.pm_other}
                onChange={(e) => setSystem("pm_other", e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="clearinghouse">Clearinghouse</label>
            <select
              id="clearinghouse"
              value={form.systems.clearinghouse}
              onChange={(e) => setSystem("clearinghouse", e.target.value)}
            >
              <option value="">Not sure</option>
              {definition.clearinghouses.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {form.systems.clearinghouse === "other" && (
            <div className="form-field">
              <label htmlFor="clearinghouse_other">Clearinghouse name</label>
              <input
                id="clearinghouse_other"
                value={form.systems.clearinghouse_other}
                onChange={(e) =>
                  setSystem("clearinghouse_other", e.target.value)
                }
              />
            </div>
          )}
        </div>

        <h2>Clearinghouses you use</h2>
        <p className="intake-muted">{definition.clearinghouse_note}</p>
        <div className="intake-clearinghouses">
          {definition.clearinghouses
            .filter((c) => c.key !== "none")
            .map((c) => {
              const entry = (form.systems.clearinghouses || []).find(
                (ch) => ch.key === c.key,
              );
              return (
                <fieldset
                  className="intake-clearinghouse"
                  key={c.key}
                  data-clearinghouse={c.key}
                >
                  <label className="intake-ch-toggle">
                    <input
                      type="checkbox"
                      checked={!!entry}
                      onChange={(e) =>
                        toggleClearinghouse(c.key, e.target.checked)
                      }
                    />
                    <span>{c.label}</span>
                  </label>
                  {entry && (
                    <div className="form-row intake-wrap">
                      {c.key === "other" && (
                        <div className="form-field">
                          <label htmlFor={`ch-${c.key}-name`}>
                            Clearinghouse name *
                          </label>
                          <input
                            id={`ch-${c.key}-name`}
                            value={entry.name}
                            onChange={(e) =>
                              setClearinghouse(c.key, { name: e.target.value })
                            }
                          />
                        </div>
                      )}
                      <div className="form-field">
                        <label htmlFor={`ch-${c.key}-access_path`}>
                          Portal URL or vendor
                        </label>
                        <input
                          id={`ch-${c.key}-access_path`}
                          value={entry.access_path}
                          onChange={(e) =>
                            setClearinghouse(c.key, {
                              access_path: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="form-field">
                        <label htmlFor={`ch-${c.key}-username`}>
                          Username Atlas would use
                        </label>
                        <input
                          id={`ch-${c.key}-username`}
                          autoComplete="off"
                          value={entry.username}
                          onChange={(e) =>
                            setClearinghouse(c.key, {
                              username: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="form-field full">
                        <div className="intake-radios">
                          <span className="intake-muted">
                            Can Atlas be given read access to your 835
                            remittance files there? *
                          </span>
                          {(definition.era_access_choices || []).map((v) => (
                            <label key={v}>
                              <input
                                type="radio"
                                name={`era-${c.key}`}
                                value={v}
                                checked={entry.era_access === v}
                                onChange={() =>
                                  setClearinghouse(c.key, { era_access: v })
                                }
                              />
                              {ERA_ACCESS_LABEL[v] || v}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="form-field full">
                        <label htmlFor={`ch-${c.key}-notes`}>
                          Notes (no passwords here; we collect those on a call)
                        </label>
                        <textarea
                          id={`ch-${c.key}-notes`}
                          rows={2}
                          value={entry.notes}
                          onChange={(e) =>
                            setClearinghouse(c.key, { notes: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  )}
                </fieldset>
              );
            })}
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="patient_identifier_type">
              Which patient identifier does your billing system use?
            </label>
            <select
              id="patient_identifier_type"
              value={form.systems.patient_identifier_type}
              onChange={(e) =>
                setSystem("patient_identifier_type", e.target.value)
              }
            >
              <option value="">Not sure</option>
              {(definition.patient_id_types || []).map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          {form.systems.patient_identifier_type === "other" && (
            <div className="form-field">
              <label htmlFor="patient_identifier_other">Identifier name</label>
              <input
                id="patient_identifier_other"
                value={form.systems.patient_identifier_other}
                onChange={(e) =>
                  setSystem("patient_identifier_other", e.target.value)
                }
              />
            </div>
          )}
        </div>

        {slots.length > 0 && (
          <>
            <h2>Access</h2>
            <p className="intake-muted">{definition.access_note}</p>
          </>
        )}
        {slots.map(({ slot, systemKey, label, loginAllowed }) => {
          const a = form.access[slot];
          const fields = (definition.credential_fields || {})[systemKey] || [];
          const apiAllowed = fields.length > 0;
          return (
            <fieldset className="intake-access" key={slot} data-slot={slot}>
              <legend>{label}</legend>
              <div className="intake-radios">
                {!apiAllowed && (
                  <p className="intake-muted">
                    Atlas has no API integration for this system yet, so login
                    access is the option: a username and password Atlas can use,
                    plus how two-factor codes are delivered.
                  </p>
                )}
                {apiAllowed && (
                  <label>
                    <input
                      type="radio"
                      name={`access-${slot}`}
                      value="api"
                      checked={a.access_method === "api"}
                      onChange={() => setAccess(slot, { access_method: "api" })}
                    />
                    API access (API / customer key + API user, preferred)
                  </label>
                )}
                {loginAllowed && (
                  <label>
                    <input
                      type="radio"
                      name={`access-${slot}`}
                      value="login"
                      checked={a.access_method === "login"}
                      onChange={() =>
                        setAccess(slot, { access_method: "login" })
                      }
                    />
                    Login access
                  </label>
                )}
                <label>
                  <input
                    type="radio"
                    name={`access-${slot}`}
                    value=""
                    checked={a.access_method === ""}
                    onChange={() => setAccess(slot, { ...EMPTY_ACCESS })}
                  />
                  Not now
                </label>
              </div>
              {a.access_method === "api" && (
                <div className="form-row intake-wrap">
                  {fields.map((f) => (
                    <div className="form-field" key={f.key}>
                      <label htmlFor={`${slot}-${f.key}`}>
                        {f.label}
                        {f.required ? " *" : ""}
                      </label>
                      <input
                        id={`${slot}-${f.key}`}
                        type={f.type === "password" ? "password" : "text"}
                        autoComplete="off"
                        value={a.fields[f.key] || ""}
                        onChange={(e) =>
                          setAccessField(slot, f.key, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
              {a.access_method === "login" && (
                <div className="form-row intake-wrap">
                  <div className="form-field">
                    <label htmlFor={`${slot}-username`}>Username *</label>
                    <input
                      id={`${slot}-username`}
                      autoComplete="off"
                      value={a.username}
                      onChange={(e) =>
                        setAccess(slot, { username: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor={`${slot}-password`}>Password *</label>
                    <input
                      id={`${slot}-password`}
                      type="password"
                      autoComplete="new-password"
                      value={a.password}
                      onChange={(e) =>
                        setAccess(slot, { password: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-field full">
                    <label htmlFor={`${slot}-mfa`}>
                      Two-factor / MFA notes (how codes are delivered, who holds
                      the device)
                    </label>
                    <textarea
                      id={`${slot}-mfa`}
                      rows={2}
                      value={a.mfa_notes}
                      onChange={(e) =>
                        setAccess(slot, { mfa_notes: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}
            </fieldset>
          );
        })}

        <h2>Agreement</h2>
        <div className="intake-baa">
          <label className="intake-check">
            <input
              type="checkbox"
              checked={form.baa.accepted}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  baa: { ...f.baa, accepted: e.target.checked },
                }))
              }
            />
            <span>
              I accept the Business Associate Agreement with Atlas and confirm I
              am authorized to share this practice's billing data and system
              access with Atlas for billing services. *
            </span>
          </label>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="baa_name">Your full name *</label>
              <input
                id="baa_name"
                value={form.baa.name}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    baa: { ...f.baa, name: e.target.value },
                  }))
                }
              />
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Submit to Atlas"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default IntakePage;
